import React, { useEffect } from "react";
import { SimpleTable } from "../../shared-components/SimpleTable";
import { formatCurrency } from "../../javascriptSucksUtils";
import {
  useProgram,
  useProgramUsage,
  useUpdateProgramMutation,
} from "../../crud/pricing/hooks";
import { SubmitHandler, useForm } from "react-hook-form";
import { ProgramChoicesEnum, ProgramSchema } from "../../api-client";
import { InputAdornment, Stack, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { HookSwitch } from "../../shared-components/HookSwitch";

type ProgramTableProps = {
  programId: number;
};

const tierToFancyName = (tier?: ProgramChoicesEnum): string => {
  switch (tier) {
    case ProgramChoicesEnum.Free:
      return "Coiled Free";
    case ProgramChoicesEnum.Payg:
      return "Pay As You Go";
    case ProgramChoicesEnum.Enterprise:
      return "Coiled Enterprise";
    default:
      return "";
  }
};

export const ProgramTable = ({
  programId,
}: ProgramTableProps): React.ReactElement | null => {
  const { data: activeProgram, isSuccess: programSuccess } =
    useProgram(programId);
  const { data: programUsage, isSuccess: programUsageSuccess } =
    useProgramUsage(programId);
  const updateProgramMutation = useUpdateProgramMutation();
  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { isDirty },
    watch,
  } = useForm<ProgramSchema>({
    defaultValues: {
      id: 0,
      spendLimitShouldStopInstances: true,
      programDollarsSpendLimit: 0,
    },
  });
  const programDollarsSpendLimit = watch("programDollarsSpendLimit");
  useEffect(() => {
    if (activeProgram) {
      reset(activeProgram);
    }
  }, [activeProgram, reset]);
  const onSubmit: SubmitHandler<ProgramSchema> = (data: ProgramSchema) => {
    updateProgramMutation.mutate(
      {
        programId: data.id,
        updateProgramSchema: {
          spendLimitShouldStopInstances: data.spendLimitShouldStopInstances,
          spendLimit: data.programDollarsSpendLimit,
        },
      },
      {
        onSuccess: () => {
          reset(data);
        },
      },
    );
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <SimpleTable
          loading={!programSuccess || !programUsageSuccess}
          text={[
            ["Current Plan", tierToFancyName(activeProgram?.tier)],
            [
              "Credit Balance",
              programUsage?.currentCredits.toLocaleString() || "0",
            ],
            [
              "Coiled Credits Used",
              programUsage?.programCreditsSpent.toLocaleString() || "0",
            ],
            [
              "Coiled Credit Monthly Allowance",
              activeProgram?.creditAllotment.toLocaleString() || "",
            ],
            [
              "Coiled Credits Allowance Used",
              `${
                programUsage?.programCreditsUsagePercent.toLocaleString() || "0"
              }%`,
            ],
            [
              "Coiled Credit Value",
              activeProgram
                ? formatCurrency(activeProgram.coiledCreditValue) || "0.05"
                : "",
            ],
            [
              "Current Monthly Spend",
              formatCurrency(programUsage?.programDollarsSpent) || "0",
            ],

            [
              "Spend Limit",
              activeProgram?.tier !== ProgramChoicesEnum.Free ? (
                <TextField
                  key="spend-limit"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {programDollarsSpendLimit === 0
                          ? "(zero means no limit)"
                          : ""}
                        $
                      </InputAdornment>
                    ),
                  }}
                  type="number"
                  size="small"
                  {...register("programDollarsSpendLimit", {
                    valueAsNumber: true,
                  })}
                />
              ) : (
                "N/A"
              ),
            ],
            [
              "Spend Limit Should Stop Instances",
              activeProgram?.tier !== ProgramChoicesEnum.Free ? (
                <HookSwitch
                  control={control}
                  name="spendLimitShouldStopInstances"
                />
              ) : (
                "N/A"
              ),
            ],
          ]}
        />
        <LoadingButton
          type="submit"
          variant="primary"
          disabled={!isDirty}
          sx={{ maxWidth: "100px" }}
        >
          Save
        </LoadingButton>
      </Stack>
    </form>
  );
};
